import { Box, Button, Modal, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { scheduledEmailsKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";

const RefreshEmailSchedulerModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { getScheduledEmails, refreshEmailScheduler } = useAPI();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");

  const scheduledJobs = useQuery({
    queryKey: scheduledEmailsKeys.all,
    queryFn: getScheduledEmails,
    select: (response) => response.data,
  });

  const refreshEmailSchedulerMutation = useMutation({
    mutationFn: refreshEmailScheduler,
    onSuccess: () => {
      Toast.success("Successfully refreshed the email scheduler");
      queryClient.invalidateQueries({
        queryKey: scheduledEmailsKeys.all,
      });
      handleClose();
    },
    onError: () => {
      Toast.error("Error refreshing the email scheduler");
    },
  });

  // Filter jobs based on search term
  const filteredJobs =
    scheduledJobs.data?.filter((job) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        job.id?.toLowerCase().includes(searchLower) ||
        job.name?.toLowerCase().includes(searchLower) ||
        job.next_run_time?.toLowerCase().includes(searchLower)
      );
    }) || [];

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    maxHeight: "90vh",
    bgcolor: colors.common_white,
    borderRadius: 2,
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    overflow: "hidden",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" mb={2}>
          Refresh Email Scheduler
        </Typography>

        {scheduledJobs.isLoading ? (
          <LoadingIcon />
        ) : scheduledJobs.data && scheduledJobs.isSuccess ? (
          <>
            <Typography mb={2}>
              Refreshing the scheduler will <strong>clear all existing</strong>{" "}
              scheduled emails from the scheduler, and re-add all existing jobs
              from the DB <em>ScheduledEmailJob</em> table.
            </Typography>

            {scheduledJobs.data.length > 0 ? (
              <>
                <Typography variant="subtitle1" mb={1}>
                  <strong>
                    Existing scheduled emails ({scheduledJobs.data.length}):
                  </strong>
                </Typography>

                {/* Search Bar */}
                <TextField
                  label="Search emails..."
                  placeholder="Search by ID, name, or schedule time"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />

                {/* Results Info */}
                {searchTerm && (
                  <Typography variant="body2" color="textSecondary" mb={1}>
                    Showing {filteredJobs.length} of {scheduledJobs.data.length}{" "}
                    emails
                  </Typography>
                )}

                {/* Scrollable Job List */}
                <Box
                  sx={{
                    maxHeight: "400px",
                    overflow: "auto",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                  }}
                >
                  {filteredJobs.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {filteredJobs.map((job) => (
                        <Box
                          key={job.id}
                          sx={{
                            border: "1px solid #f0f0f0",
                            borderRadius: 1,
                            p: 2,
                            backgroundColor: "#fafafa",
                          }}
                        >
                          <Typography variant="body2" component="div">
                            <strong>ID:</strong> {job.id}
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>Name:</strong> {job.name}
                          </Typography>
                          <Typography variant="body2" component="div">
                            <strong>Next Run:</strong> {job.next_run_time}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography color="textSecondary" textAlign="center">
                      No emails match your search term "{searchTerm}"
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <Typography mb={2}>
                No jobs currently exist in the email scheduler
              </Typography>
            )}

            {refreshEmailSchedulerMutation.isPending ? (
              <LoadingIcon />
            ) : (
              <Box display="flex" justifyContent="center" gap={2}>
                <PrimaryButton
                  size="medium"
                  onClick={() => refreshEmailSchedulerMutation.mutate()}
                >
                  Refresh Scheduler
                </PrimaryButton>
                <PrimaryButton
                  size="medium"
                  onClick={handleClose}
                  sx={{ backgroundColor: "white", color: colors.primary_main }}
                >
                  Cancel
                </PrimaryButton>
              </Box>
            )}
          </>
        ) : (
          <Typography>Error loading scheduled emails</Typography>
        )}
      </Box>
    </Modal>
  );
};

export default function RefreshEmailScheduler() {
  const colors = useColors();
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <Button
        variant="outlined"
        sx={{
          backgroundColor: colors.secondary_main,
          "&:hover": {
            backgroundColor: colors.secondary_dark,
          },
        }}
        color="primary"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        Refresh Email Scheduler
      </Button>
      {isModalOpen && (
        <RefreshEmailSchedulerModal
          open={isModalOpen}
          handleClose={() => {
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
}
